import type { Icon } from "@phosphor-icons/react";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text, type TextProps } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { IconTile, type IconTileProps } from "./icon-tile";
import { StatDelta, type StatDeltaProps } from "./stat-delta";

export interface StatTileProps {
  icon: Icon;
  iconColor?: IconTileProps["color"];
  label: string;
  value: React.ReactNode;
  valueTone?: TextProps["tone"];
  unit?: string;
  delta?: Omit<StatDeltaProps, "className">;
  comparison?: string;
  caption?: React.ReactNode;
  action?: React.ReactNode;
  isLoading?: boolean;
  className?: string;
}

export const StatTile = ({
  icon,
  iconColor,
  label,
  value,
  valueTone,
  unit,
  delta,
  comparison,
  caption,
  action,
  isLoading,
  className,
}: StatTileProps) => (
  <Stack
    align="start"
    gap={4}
    className={cn(
      "w-full",
      cn("min-w-0 flex-1 bg-surface-raised p-4", className),
    )}
  >
    <Stack direction="row" align="center" gap={2} className="min-w-0">
      <IconTile icon={icon} color={iconColor} />
      <Text variant="label" className="truncate">
        {label}
      </Text>
    </Stack>
    <Stack
      className="w-full"
      direction="row"
      align="end"
      justify="between"
      gap={4}
    >
      <Stack align="start" gap={1} className="w-full min-w-0">
        {isLoading ? (
          <Skeleton className="h-10 w-24" />
        ) : (
          <Stack direction="row" align="baseline" gap={1}>
            <Text variant="stat" tone={valueTone}>
              {value}
            </Text>
            {unit && (
              <Text variant="caption" tone="muted">
                {unit}
              </Text>
            )}
          </Stack>
        )}
        {(delta || comparison) && (
          <Stack
            className="w-full"
            gap={4}
            direction="row"
            align="center"
            justify="between"
          >
            {delta && !isLoading ? <StatDelta {...delta} /> : <span />}
            {comparison && !isLoading && (
              <Text variant="caption" tone="muted">
                {comparison}
              </Text>
            )}
          </Stack>
        )}
        {caption && !isLoading && (
          <Text
            variant="caption"
            tone="muted"
            className="flex items-center gap-1"
          >
            {caption}
          </Text>
        )}
      </Stack>
      {action}
    </Stack>
  </Stack>
);
